import { useCallback } from 'react';
import moment from 'moment';
import { NEW_DATA_ENTRY, NEW_QA_REVIEW } from '../../../../routeNames.js';
import { getNewPeriodDates, gridNewCalculate, patientPayCalculateClaim, updateFormValues } from '../utils';
import { validatePPFull } from '../schemas/validationSchemaPP';
import { setRHFErrors, validateAllFields835Lte } from '../schemas/validationSchema835Lte';
import { validateAllFieldsCorres } from '../schemas/validationSchemaCorres';
import Swal from 'sweetalert2';
import NewGrid from '../NewGrid';
import { setEditedDataInRHF } from '../utils/createEditedDataQASwitchTran';
import { processPlbAdjustments } from '../utils/index1';
import { getValidateTaxIdData } from '../../../../redux/grid/action.js';

// Utility functions
const pathname = window.location.pathname;
const parseAmount = (value, returnType = 'number') => {
  if (value === "" || value === null || value === undefined) {
    return returnType === 'string' ? "" : parseFloat(0).toFixed(2);
  }
  const parsed = parseFloat(value || 0);
  return returnType === 'string' ? parsed.toString() : parsed.toFixed(2);
};

const formatAmountValue = (value) => {
  return value === "" || value === null || value === undefined ? "" : parseFloat(value || 0).toFixed(2);
};

const getPageValue = (jobData) => {
  return jobData?.is_large_batch_job && jobData?.header_range?.length > 0 
    ? (jobData.header_range.length + 2).toString()
    : "1";
};

// Initial claim structure factory
export const createInitialClaim = (jobData, values,is_serviceline=true, isLite = false) => {
  const pageValue = getPageValue(jobData);
  const statusCode = values?.transaction_type === "PATIENT_PAY_835" ? "1" : "";
  const initialFinancialFields = isLite ? 
    ['claim_paid', 'claim_balance', 'sl_paid', 'sl_balance', 'sl_total'] : 
    [
      'claim_billed', 'claim_discount', 'claim_allowed', 'claim_deduct', 
      'claim_coins', 'claim_copay', 'claim_cob', 'claim_patres', 
      'claim_paid', 'claim_balance', 'claim_adjustment_total', 'claim_adjustment_total_pr',
      'sl_billed', 'sl_discount', 'sl_allowed', 'sl_deduct', 'sl_coins', 
      'sl_copay', 'sl_cob', 'sl_patres', 'sl_amount', 'sl_paid', 'sl_balance', 'sl_total'
    ];

  return {
    edited: true,
    subscriber: true,
    rendering_provider: true,
    order: "",
    claim_id: "",
    patient_account_number: "",
    payer_claim_number: "",
    period_start: "",
    period_end: "",
    status_code: statusCode,
    page_from: pageValue,
    page_to: pageValue,

    // Financial fields
    ...Object.fromEntries(initialFinancialFields.map(field => [field, parseFloat(0).toFixed(2)])),

    mrn: "",
    statement_number: "",
    statement_date: "",

    // Patient info
    ...Object.fromEntries([
      'patient_id', 'patient_first_name', 'patient_middle_name', 'patient_last_name', 
      'patient_identifier_code', 'prior_auth_code'
    ].map(field => [field, ""])),

    // Subscriber info
    ...Object.fromEntries([
      'subscriber_id', 'subscriber_first_name', 'subscriber_middle_name', 'subscriber_last_name', 
      'subscriber_identifier_code'
    ].map(field => [field, ""])),

    // Rendering provider info
    ...Object.fromEntries([
      'rendering_provider_id', 'rendering_provider_first_name', 'rendering_provider_last_name', 
      'rendering_provider_identifier_code'
    ].map(field => [field, ""])),

    claim_adjustments: [{
      pc: "",
      group_code: "",
      reason_code: "",
      rmk: "",
      amount: "",
      claim_adjustment_id: "",
      edited: true,
    }],

    service_lines: !is_serviceline?[]:[{
      edited: true,
      order: "",
      service_line_id: "",
      from_date: "",
      to_date: "",
      code: "",
      procedure_type: "HC",
      modifier1: "",
      modifier2: "",
      modifier3: "",
      modifier4: "",
      rev_code: "",
      units: "",
      patres: "",
      billed: "",
      discount: "",
      allowed: "",
      deduct: "",
      coins: "",
      copay: "",
      cob: "",
      paid: "",
      balance: "",
      adjustments: [{
        edited: true,
        adjustment_id: "",
        pc: "",
        group_code: "",
        reason_code: "",
        rmk: "",
        amount: "",
      }],
    }],
  };
};

// Process existing claims with automation
const processClaimsAutomation = (values, jobData, isPullNeeded = true, isPatientPay = false, isLite = false) => {
  const is_worked = true;
  const isAutomationDataLoad = jobData.is_automation_completed && jobData.is_automation_job;
  let pulled = false;
  const QDX_TRANSACTION = values?.transaction_type;

  const needCalculate = (values?.transaction_type !== "PATIENT_PAY_835") && isAutomationDataLoad && pathname == NEW_DATA_ENTRY;
  
  const updatedClaims = values?.claims?.map((claim_item, index) => {
    const claim = needCalculate ? gridNewCalculate(claim_item, values?.transaction_type) :isPatientPay? patientPayCalculateClaim(claim_item): { ...claim_item };

    claim.edited = !is_worked

    // Handle encounter ID
    if (claim?.encounter_id) {
      claim.encounter = claim.encounter_id || "";
    }

    // Basic claim setup
    const pageValue = getPageValue(jobData);
    claim.page_from = claim?.page_from || pageValue;
    claim.page_to = claim?.page_to || pageValue;

    // Set basic fields
    claim.claim_id = claim.claim_id ?? "";
    claim.prior_auth_code = claim.prior_auth_code ?? "";
    claim.payer_claim_number = claim.payer_claim_number ?? "";

    const hasSubscriberInfo = () => {
      return claim.subscriber_id ||
        ((claim?.subscriber_first_name || claim?.subscriber_middle_name || 
          claim?.subscriber_last_name || claim?.subscriber_identifier_code)) ||
        (isAutomationDataLoad && (claim?.subscriber_first_name || claim?.subscriber_middle_name || 
          claim?.subscriber_last_name || claim?.subscriber_identifier_code));
    };

    const hasProviderInfo = () => {
      return claim.rendering_provider_id ||
        ((claim?.rendering_provider_first_name || 
          claim?.rendering_provider_last_name || claim?.rendering_provider_identifier_code)) ||
        (isAutomationDataLoad && (claim?.rendering_provider_first_name || 
          claim?.rendering_provider_last_name || claim?.rendering_provider_identifier_code));
    };

    claim.subscriber = hasSubscriberInfo();
    claim.rendering_provider = hasProviderInfo();

    // Date formatting with validation
    claim.period_start = (claim.period_start && claim.period_start !== "" && moment(claim.period_start).isValid()) 
      ? moment(claim.period_start).format("MM/DD/YYYY") : "";
    claim.period_end = (claim.period_end && claim.period_end !== "" && moment(claim.period_end).isValid()) 
      ? moment(claim.period_end).format("MM/DD/YYYY") : "";

    claim.encounter = claim?.encounter || "";

    // Financial fields processing - different logic for Patient Pay and Lite
    if (isPatientPay) {
      // Patient Pay specific financial logic
      if (claim?.service_lines?.length > 0) {
        claim.claim_billed = parseFloat(claim?.service_lines?.reduce((acc, serviceLine) => 
          acc + (Number.isNaN(parseFloat(serviceLine.billed)) ? 0 : parseFloat(serviceLine.billed)), 0)).toFixed(2);
        claim.claim_paid = parseFloat(claim?.service_lines?.reduce((acc, serviceLine) => 
          acc + (Number.isNaN(parseFloat(serviceLine.paid)) ? 0 : parseFloat(serviceLine.paid)), 0)).toFixed(2);
      } else {
        claim.claim_billed = claim?.claim_billed >= 0 && claim?.claim_billed !== '' ? parseFloat(claim?.claim_billed).toFixed(2) : '';
        claim.claim_paid = claim?.claim_paid >= 0 && claim?.claim_paid !== '' ? parseFloat(claim?.claim_paid).toFixed(2) : '';
      }
      
      // Set other financial fields to zero for patient pay
      claim.claim_discount = parseFloat(0).toFixed(2);
      claim.claim_allowed = parseFloat(0).toFixed(2);
      claim.claim_deduct = parseFloat(0).toFixed(2);
      claim.claim_coins = parseFloat(0).toFixed(2);
      claim.claim_copay = parseFloat(0).toFixed(2);
      claim.claim_cob = parseFloat(0).toFixed(2);
      claim.claim_adjustment_total = parseFloat(0).toFixed(2);
      claim.claim_adjustment_total_pr = parseFloat(0).toFixed(2);
      
      // Calculate balance
      claim.claim_balance = parseFloat((claim?.claim_billed ? claim?.claim_billed : 0) - (claim?.claim_paid ? claim?.claim_paid : 0)).toFixed(2);
    } else if (isLite) {
      const alwaysAmountFields = ['claim_patres', 'claim_balance', 'claim_paid'];
      alwaysAmountFields.forEach(field => {
        claim[field] = parseAmount(claim?.[field]);
      });
    } else {
      // Regular 835 processing
      const isPatientPayTransaction = jobData?.grid_data?.transaction_type === 'PATIENT_PAY_835';
      
      claim.claim_billed = isPatientPayTransaction && claim?.claim_billed === '' ? '' : parseAmount(claim?.claim_billed);
      claim.claim_paid = isPatientPayTransaction && claim?.claim_paid === '' ? '' : 
        claim?.claim_paid === '' ? '' : parseAmount(claim?.claim_paid);

      // Process other financial fields
      const conditionalAmountFields = ['claim_discount', 'claim_allowed', 'claim_deduct', 'claim_coins', 'claim_copay', 'claim_cob', 'claim_adjustment_total'];
      const alwaysAmountFields = ['claim_patres', 'claim_balance', 'claim_adjustment_total_pr'];

      conditionalAmountFields.forEach(field => {
        claim[field] = claim?.[field] === "" ? "" : parseAmount(claim?.[field]);
      });

      alwaysAmountFields.forEach(field => {
        claim[field] = parseAmount(claim?.[field]);
      });
    }

    // Service Line financial fields
    const slAmountFields = isLite ? 
      ['sl_paid', 'sl_balance', 'sl_total'] : 
      ['sl_billed', 'fake_sl_patres', 'adjustment_total', 'sl_allowed', 'sl_deduct', 'sl_coins', 'sl_copay', 'sl_cob', 'sl_patres', 'sl_amount', 'sl_paid', 'sl_balance', 'sl_total'];
    slAmountFields.forEach(field => {
      claim[field] = parseAmount(claim?.[field]);
    });

    // Status code
    claim.status_code = isPatientPay && !claim?.status_code ? 1 : (claim?.status_code && !isNaN(claim?.status_code) ? claim?.status_code : "1");

    // Ensure all name fields are strings
    const nameFields = [
      'patient_first_name', 'patient_middle_name', 'patient_last_name', 'patient_identifier_code',
      'prior_auth_code', 'subscriber_first_name', 'subscriber_middle_name', 'subscriber_last_name',
      'subscriber_identifier_code', 'rendering_provider_first_name', 'rendering_provider_last_name',
      'rendering_provider_identifier_code', 'mrn', 'statement_number', 'statement_date'
    ];

    nameFields.forEach(field => {
      claim[field] = claim?.[field] || "";
    });

    // Additional automation fields
    const automationFields = [
      'patient_account_number', 'payer_claim_number', 'status_code'
    ];

    automationFields.forEach(field => {
      claim[field] = claim?.[field] || "";
    });

    // Process claim 
    if(isPatientPay){
        claim.claim_adjustments = []
    }
    else if (claim?.claim_adjustments?.length > 0) {
      claim.claim_adjustments.forEach((adjustment, claimAdjIndex) => {
        adjustment.amount = adjustment.amount === "" ? "" : parseAmount(adjustment.amount);
        adjustment.edited = !is_worked
      });
    } else {
      // You'll need to define formEob.initialValues or pass it as parameter
      claim.claim_adjustments = [{ pc: "", group_code: "", reason_code: "", rmk: "", amount: "",edited : true }];
    }

    // Process service lines
    if (claim?.service_lines?.length > 0 && Array.isArray(claim.service_lines)) {
      // Get period dates from service lines for NEW_DATA_ENTRY path
      if (pathname === NEW_DATA_ENTRY) {
        const { period_start, period_end } = getNewPeriodDates(claim.service_lines);
        claim.period_start = period_start || claim.period_start;
        claim.period_end = period_end || claim.period_end;
      }
      if(!isPatientPay){
            claim.service_lines.forEach((serviceline, servicelineIndex) => {
                serviceline.edited = !is_worked;

                // Date formatting
                serviceline.from_date = serviceline?.from_date 
                ? moment(serviceline.from_date).format("MM/DD/YYYY") : "";
                serviceline.to_date = serviceline?.to_date 
                ? moment(serviceline.to_date).format("MM/DD/YYYY") : "";

                // Process financial fields
                serviceline.billed = parseAmount(serviceline?.billed);
                serviceline.patres = parseAmount(serviceline?.patres);
                serviceline.balance = parseAmount(serviceline?.balance);
                serviceline.units = parseAmount(serviceline?.units, 'string');

                // Format amount fields
                const formatFields = isLite ? ['paid'] : ['discount', 'allowed', 'deduct', 'coins', 'copay', 'cob', 'paid'];
                formatFields.forEach(field => {
                serviceline[field] = formatAmountValue(serviceline?.[field]);
                });

                // Set modifier and other fields
                const stringFields = ['modifier1', 'modifier2', 'modifier3', 'modifier4', 'rev_code'];
                stringFields.forEach(field => {
                serviceline[field] = serviceline?.[field] || "";
                });

                serviceline.procedure_type = serviceline?.procedure_type || "HC";

                // Process adjustments
                if (serviceline?.adjustments?.length > 0) {
                serviceline.adjustments.forEach((adjustment, adjIndex) => {
                    adjustment.amount = formatAmountValue(adjustment.amount);
                    adjustment.edited = !is_worked;

                    const adjustmentFields = ['pc', 'group_code', 'reason_code', 'rmk', 'description'];
                    adjustmentFields.forEach(field => {
                    adjustment[field] = (adjustment?.[field] !== undefined && adjustment?.[field] !== null) 
                        ? adjustment[field] : "";
                    });
                });
                } else {
                serviceline.adjustments = [{ adjustment_id: "", pc: "", group_code: "", reason_code: "", rmk: "", amount: "",edited : true }];
                }
            });
      }
    } else if (!isPatientPay && jobData?.is_worked === false && pathname === NEW_DATA_ENTRY) {
      claim.service_lines = [createInitialClaim(jobData, values,isPatientPay?false:true, isLite).service_lines[0]];
    }
    if(isPatientPay){
        claim.service_lines = []
    }
    // Check if data was pulled
    if (claim?.encounter && claim.encounter !== '') {
      pulled = true;
    }

    return isPatientPay? patientPayCalculateClaim(claim):claim;
  });

  return { claims: updatedClaims, pulled };
};

// Calculate totals
const calculateRemainingPlbPaidTotals = (claims, plb_total=0, check_amount,claim_paid='claim_paid') => {
    // Pre-convert check_amount once
  const checkAmount = +check_amount || 0;

  // Optimize claims calculation
  let paid_total = 0;
  for (let i = 0; i < claims.length; i++) {
    const claimPaid = claims[i]?.[claim_paid];
    if (claimPaid) paid_total += +claimPaid;
  }

  // Optimize PLB calculation 
//   let plb_total = 0;
//   if (plbAdjustments?.length) {
//     for (let i = 0; i < plbAdjustments.length; i++) {
//       const plbAmt = plbAdjustments[i].plb_amt;
//       if (plbAmt) plb_total += +plbAmt;
//     }
//   }

  // Calculate remaining
    let remaining =
              parseFloat(checkAmount ? checkAmount : 0) -
              parseFloat(paid_total ? paid_total : 0) +
              parseFloat(plb_total ? plb_total : 0);

// Handle -0.00 case more efficiently
  if (remaining === 0 || (remaining > -0.005 && remaining < 0.005)) {
    remaining = 0.00;
  }

  return {
    paid_total,
    plb_total,
    remaining: remaining.toFixed(2)
  };
}

// Helper function for PLB only remaining balance
const getPlbOnlyRemainingBalance = (plb_adjustments, check_amount) => {
  const old_check_amount = parseFloat(check_amount ? check_amount : 0);
  let plb_amt = 0;
  if (plb_adjustments?.length > 0) {
    plb_adjustments?.map((plb, index) => {
      plb_amt += parseFloat(plb?.plb_amt ? plb?.plb_amt : 0);
    });
  }
  return parseFloat(parseFloat(old_check_amount ?? 0) + parseFloat(plb_amt ?? 0)).toFixed(2);
};

// Optimized and reusable payment data generator
const generatePaymentData = (paymentType, isCheckNumber, jobData, values) => {
  // Get data source based on type
  const isGeneratedCheckNumber = jobData?.client_level_customization?.is_generated_check_number;
  const dataSource = getDataSource(paymentType, jobData, values);
  const pathname = window.location.pathname
  // Handle CORRESPONDENCE_835 type separately (simplest case)
  if (paymentType === "CORRESPONDENCE_835") {
    return {
      payment_method: "COR",
      check_number: generateCheckNumber("LT", dataSource.timezone, isCheckNumber, isGeneratedCheckNumber, pathname, dataSource.check_number)
    };
  }
  
  // Handle PATIENT_PAY_835, PAYMENT835, and PAYMENT835_LITE
  return processPaymentLogic(dataSource, isCheckNumber, isGeneratedCheckNumber, pathname, paymentType);
};

// Get appropriate data source based on type
const getDataSource = (paymentType, jobData, values) => {
  switch (paymentType) {
    case "PATIENT_PAY_835":
      return {
        payment_method: values?.payment_method,
        check_amount: values?.check_amount,
        check_number: values?.check_number,
        timezone: values?.timezone
      };
    case "PAYMENT835":
    case "PAYMENT835_LITE":
      return {
        payment_method: values?.payment_method,
        check_amount: values?.check_amount,
        check_number: values?.check_number,
        timezone: jobData?.grid_data?.timezone
      };
    case "CORRESPONDENCE_835":
      return {
        payment_method: null,
        check_amount: null,
        check_number: values?.check_number,
        timezone: jobData?.grid_data?.timezone
      };
    default:
      return {};
  }
};

// Process main payment logic (reusable for PATIENT_PAY_835, PAYMENT835, and PAYMENT835_LITE)
const processPaymentLogic = (dataSource, isCheckNumber, isGeneratedCheckNumber, pathname, paymentType) => {
  const { payment_method: sourcePaymentMethod, check_amount, check_number, timezone } = dataSource;
  // Handle case when no payment method is provided (only for PATIENT_PAY_835)
  if (paymentType === "PATIENT_PAY_835" && !sourcePaymentMethod) {
    return {
      payment_method: "CHK",
      check_number: generateCheckNumber("RM", timezone, isCheckNumber, isGeneratedCheckNumber, pathname, check_number)
    };
  }

  let finalPaymentMethod = sourcePaymentMethod || "";

  // Handle COR payment method
  if (finalPaymentMethod === "COR") {
    finalPaymentMethod = "NON";
    return {
      payment_method: finalPaymentMethod,
      check_number: generateCheckNumber("RX", timezone, isCheckNumber, isGeneratedCheckNumber, pathname, check_number)
    };
  }

  // Handle based on check amount
  return processAmountBasedLogic(finalPaymentMethod, check_amount, check_number, timezone, isCheckNumber, isGeneratedCheckNumber, pathname, paymentType);
};

// Process logic based on check amount
const processAmountBasedLogic = (paymentMethod, checkAmount, checkNumber, timezone, isCheckNumber, isGeneratedCheckNumber, pathname, paymentType) => {
  const amount = checkAmount?parseFloat(checkAmount || 0):"";

  // Case 1: Amount > 0
  if (checkAmount && amount > 0) {
    return {
      payment_method: paymentMethod || "CHK",
      check_number: checkNumber || ""
    };
  }

  // Case 2: Amount = 0 and in NEW_DATA_ENTRY
  if (pathname === NEW_DATA_ENTRY && (checkAmount === "0.00" || amount === 0)) {
    return {
      payment_method: (paymentType === "PATIENT_PAY_835" || paymentType === "PAYMENT835_LITE") ? (paymentMethod || "NON") : "NON",
      check_number: generateCheckNumber("RX", timezone, isCheckNumber, isGeneratedCheckNumber, pathname, checkNumber)
    };
  }

  // Case 3: Default case
  const defaultPaymentMethod = (paymentType === "PATIENT_PAY_835" || paymentType === "PAYMENT835_LITE") ? (paymentMethod || "ACH") : (paymentMethod || "");
  return {
    payment_method: defaultPaymentMethod,
    check_number: generateCheckNumber("RM", timezone, isCheckNumber, isGeneratedCheckNumber, pathname, checkNumber)
  };
};

// Reusable check number generator
const generateCheckNumber = (prefix, timezone, isCheckNumber, isGeneratedCheckNumber, pathname, existingCheckNumber) => {
  // If conditions are met for generation, return generated number 
  if(isCheckNumber){
    return existingCheckNumber || "";
  }
  else if (!isCheckNumber && (pathname === NEW_DATA_ENTRY||pathname === NEW_QA_REVIEW) && isGeneratedCheckNumber) {
    return prefix + timezone;
  }
  
  // Otherwise return existing check number or empty string
  return existingCheckNumber || "";
};


// Main custom hook
export const useTransactionTypes = ({
  updateMasterValues,
  eobFormGetValues,
  setControlledPosition,
  setActiveclaim835Index,
  additionalInfoSetValue,
  additionalInfoValues,
  jobData,
  handleCleareOrSettSearched837filter,
  formValues, // Added for Patient Pay
  clearServiceLines, // Added for Patient Pay
  setFullClaimData,
  triggerEobValidation,
  resetToInitailStates,
  contextValues,
  eobSetError,
  clearErrors,
  setFullClaimTotal,
  eobFormSetValues,
  setPagenateInputVal,
  isLoadFromCacheRef,
}) => {
  const isFirstJob = jobData?.is_first_sub_job;
  const isSubJob = jobData?.is_sub_job;
  const pathname = window.location.pathname;
    const isQaScreen = pathname === NEW_QA_REVIEW
  


const validateNpiTinInitial = ( facility, tin, npi) => {
  const formValues = eobFormGetValues()
  if (formValues?.qdx_status === "INCOMPLETE") return null;
  if(facility &&( tin?.length === 9 || npi?.length ===10) && [NEW_DATA_ENTRY ,NEW_QA_REVIEW].includes(window.location.pathname) &&( formValues?.edited_data?.payee_npi?.edi !== npi || formValues?.edited_data?.tax_identification_number?.edi !== tin )  )

   getValidateTaxIdData([facility], tin, npi)((response) => {
          if (response?.status === true) {
            if (response?.npi) {
              eobSetError("payee_npi", {
                type: "manual",
                message: "NPI validation failed",
              });
              
            } else {
              clearErrors("payee_npi");
            }
            
            // ✅ If TIN mismatch
            if (response?.tin) {
              eobSetError("tax_identification_number", {
                type: "manual",
                message: "Tax ID validation failed",
              });
            } else {
              clearErrors("tax_identification_number");
            }
            
            eobFormSetValues('is_valid_tax_identification_number', response?.tin, { shouldDirty: true, shouldValidate: false });
            eobFormSetValues('is_valid_payee_npi', response?.npi, { shouldDirty: true, shouldValidate: false });

            if (response?.npi || response?.tin) {
              let title = null;

              if (response?.npi && response?.tin) {
                title = "Tax ID & NPI is Mismatched";
              } else if (response?.npi) {
                title = "NPI is Mismatched";
              } else if (response?.tin) {
                title = "Tax ID is Mismatched";
              }

              Swal.fire({
                icon: "warning",  
                title: title,
                confirmButtonText: "OK",
                confirmButtonColor: "#6ba2b6", 
              });
            }
          }
        });
}



  // Enhanced handleClaimsAutomation function
    const handleClaimsAutomationOptimized = useCallback((values, transactionType = "PAYMENT835", isPullNeeded = true, isLite = false) => {
        const isPatientPay = transactionType === "PATIENT_PAY_835";
        return processClaimsAutomation(values, jobData, isPullNeeded, isPatientPay, isLite);
    }, [jobData]);

    const transactionPayment835 = useCallback(async (paymentType) => {
        const currentValues = eobFormGetValues();
        
        // Initial setup
        handleCleareOrSettSearched837filter('cleare');
        setActiveclaim835Index(0);
        
        if (localStorage.getItem('837_pulled') && !isLoadFromCacheRef.current ) { // checking if loaded from cache
            localStorage.removeItem("837_pulled");
        }

        const values = structuredClone(jobData?.grid_data);
        
        const isGeneratedCheckNumber = jobData?.client_level_customization?.is_generated_check_number;
        const isCheckNumber = values?.check_number;
        // Set position based on customization
        const yPosition = currentValues?.grid_customization === "PLB_ONLY" ? 770 : 580;
        setControlledPosition({ x: 0, y: yPosition });

        if (!isSubJob || (isSubJob && isFirstJob)) {
        // First job or non-sub job processing
        await handleFirstJobTransaction(paymentType,currentValues, values, jobData, isFirstJob,isGeneratedCheckNumber,isCheckNumber);
        } else {
        // Sub job processing
        await handleSubJobTransaction(paymentType,currentValues, values, jobData, isFirstJob,isGeneratedCheckNumber,isCheckNumber);
        }

        // Update additional info
        const additionalInfoUpdates = {
        ...additionalInfoValues,
        status: values?.qdx_status || "",
        reason: values?.qdx_reason?.length > 0 ? values.qdx_reason : [],
        document_classification: values?.qdx_classification || "",
        is_forced_balancing: values?.is_forced_balancing || false,
        is_missing_check_image: values?.is_missing_check_image || false,
        is_orphaned_check: values?.is_orphaned_check || false,
        is_payer_black_listing: values?.is_payer_black_listing || false,
        is_payer_excluding_indexing: values?.is_payer_excluding_indexing || false,
        };
        // if(jobData?.grid_data?.transaction_type !== paymentType && isQaScreen){ 
        //   setEditedDataInRHF( {data:eobFormGetValues(), transactionType:, setValue:eobFormSetValues , jobData})
        // }

        updateFormValues(additionalInfoSetValue, additionalInfoValues, additionalInfoUpdates);

        validateNpiTinInitial(jobData?.facility_id,jobData?.grid_data?.tax_identification_number,jobData?.grid_data?.payee_npi)
        setPagenateInputVal(1)
        isLoadFromCacheRef.current = false //make the load from cache false 


    }, [jobData, eobFormGetValues, setControlledPosition, setActiveclaim835Index, 
        additionalInfoSetValue, additionalInfoValues, handleCleareOrSettSearched837filter, handleClaimsAutomationOptimized ,validateNpiTinInitial]);

    const transactionPayment835Lite = useCallback(async (paymentType) => {
        handleCleareOrSettSearched837filter('cleare');
        setActiveclaim835Index(0);

        const values = structuredClone(jobData?.grid_data);
        const isGeneratedCheckNumber = jobData?.client_level_customization?.is_generated_check_number;
        const isCheckNumber = values?.check_number;

        const yPosition = formValues?.grid_customization === "PLB_ONLY" ? 770 : 650;
        setControlledPosition({ x: 0, y: yPosition });

        await handlePayment835LiteJob(paymentType, values, jobData, isFirstJob, isCheckNumber, isGeneratedCheckNumber);

        const additionalInfoUpdates = {
        ...additionalInfoValues,
        status: values?.qdx_status || "",
        reason: values?.qdx_reason?.length > 0 ? values.qdx_reason : [],
        document_classification: values?.qdx_classification || "",
        is_forced_balancing: values?.is_forced_balancing || false,
        is_missing_check_image: values?.is_missing_check_image || false,
        is_orphaned_check: values?.is_orphaned_check || false,
        is_payer_black_listing: values?.is_payer_black_listing || false,
        is_payer_excluding_indexing: values?.is_payer_excluding_indexing || false,
        };

        updateFormValues(additionalInfoSetValue, additionalInfoValues, additionalInfoUpdates);
        setPagenateInputVal(1)
      const validation = async () => {
        const formData = eobFormGetValues(); // 👈 grab latest form values
        const result = await validateAllFields835Lte(formData, contextValues);

        clearErrors();
        if (!result?.isValid) {

          setRHFErrors(result, eobSetError);

        }
      };
      validation()


    }, [jobData, eobFormGetValues, setControlledPosition, setActiveclaim835Index, 
        additionalInfoSetValue, additionalInfoValues, handleCleareOrSettSearched837filter, formValues, handleClaimsAutomationOptimized]);


    const transactionPatientPay835 = useCallback(async (paymentType) => {
    handleCleareOrSettSearched837filter('cleare');
    setActiveclaim835Index(0);
    
    const currentValues = eobFormGetValues();
    const values = structuredClone(jobData?.grid_data);
    const isCheckNumber = values?.check_number;
    const isGeneratedCheckNumber = jobData?.client_level_customization?.is_generated_check_number;
    
    // Set position based on customization
    if (formValues?.grid_customization == "PLB_ONLY") {
      setControlledPosition({ x: 0, y: 770 });
    } else {
      setControlledPosition({ x: 0, y: 650 });
    }

    if (!isSubJob || (isSubJob && isFirstJob)) {
      await handlePatientPay835FirstJob(paymentType,currentValues, values, jobData, isFirstJob, isCheckNumber, isGeneratedCheckNumber);
    } else {
      await handlePatientPay835SubJob(paymentType,currentValues, values, jobData, isFirstJob, isCheckNumber, isGeneratedCheckNumber);
    }

    // Clear service lines if function is provided
    if (clearServiceLines) {
      clearServiceLines();
    }

    // Update additional info
    const additionalInfoUpdates = {
      ...additionalInfoValues,
      status: values?.qdx_status || "",
      reason: values?.qdx_reason?.length > 0 ? values?.qdx_reason : [],
      document_classification: values?.qdx_classification || "",
      is_forced_balancing: values?.is_forced_balancing || false,
      is_missing_check_image: values?.is_missing_check_image || false,
      is_orphaned_check: values?.is_orphaned_check || false,
      is_payer_black_listing: values?.is_payer_black_listing || false,
      is_payer_excluding_indexing: values?.is_payer_excluding_indexing || false,
    };
    setPagenateInputVal(1)
    updateFormValues(additionalInfoSetValue, additionalInfoValues, additionalInfoUpdates);
  }, [jobData, eobFormGetValues, setControlledPosition, setActiveclaim835Index, 
      additionalInfoSetValue, additionalInfoValues, handleCleareOrSettSearched837filter, formValues, clearServiceLines]);

    const transactionCorrespondance = useCallback(async (paymentType) => {
        handleCleareOrSettSearched837filter('cleare');
        setActiveclaim835Index(0);

        const values = structuredClone(jobData?.grid_data);
        const yPosition = 650;
        setControlledPosition({ x: 0, y: yPosition });

        await handleCorresJob(values, jobData, isFirstJob);

        const additionalInfoUpdates = {
            ...additionalInfoValues,
            status: values?.qdx_status || "",
            reason: values?.qdx_reason?.length > 0 ? values.qdx_reason : [],
            document_classification: values?.qdx_classification || "",
            is_forced_balancing: values?.is_forced_balancing || false,
            is_missing_check_image: values?.is_missing_check_image || false,
            is_orphaned_check: values?.is_orphaned_check || false,
            is_payer_black_listing: values?.is_payer_black_listing || false,
            is_payer_excluding_indexing: values?.is_payer_excluding_indexing || false,
        };
        setPagenateInputVal(1)
        updateFormValues(additionalInfoSetValue, additionalInfoValues, additionalInfoUpdates);

      const validation = async () => {
        const formData = eobFormGetValues(); // 👈 grab latest form values
        const result = await validateAllFieldsCorres(formData, contextValues);

        clearErrors();
        if (!result?.isValid) {

          setRHFErrors(result, eobSetError);

        }
      };
      validation()


    }, [jobData, eobFormGetValues, setControlledPosition, setActiveclaim835Index,
        additionalInfoSetValue, additionalInfoValues, handleCleareOrSettSearched837filter]);

    const transactionPatientPay = useCallback(async (paymentType) => {
        handleCleareOrSettSearched837filter('cleare');
        setActiveclaim835Index(0);

        const values = structuredClone(jobData?.grid_data);
        const yPosition = 650;
        setControlledPosition({ x: 0, y: yPosition });

        await handlePatientPayJob(values, jobData);

        const additionalInfoUpdates = {
            ...additionalInfoValues,
            status: values?.qdx_status || "",
            reason: values?.qdx_reason?.length > 0 ? values.qdx_reason : [],
            document_classification: values?.qdx_classification || "",
            is_forced_balancing: values?.is_forced_balancing || false,
            is_missing_check_image: values?.is_missing_check_image || false,
            is_orphaned_check: values?.is_orphaned_check || false,
            is_payer_black_listing: values?.is_payer_black_listing || false,
            is_payer_excluding_indexing: values?.is_payer_excluding_indexing || false,
        };
      setPagenateInputVal(1)
      updateFormValues(additionalInfoSetValue, additionalInfoValues, additionalInfoUpdates);
     
      const validationPP = async () => {
        const formData = eobFormGetValues(); // 👈 grab latest form values
        const result = validatePPFull(formData, contextValues)

        if (!result?.isValid) {
          // clear old errors
          // clearErrors();

          Promise.resolve().then(() => {
            Object.entries(result).forEach(([field, message]) => {
              eobSetError(field, { type: "custom", message });
            });
          });
        }
      };

      validationPP()

    }, [jobData, setControlledPosition, setActiveclaim835Index, additionalInfoSetValue, additionalInfoValues, handleCleareOrSettSearched837filter]);

  const handleFirstJobTransaction = async (paymentType,currentValues, values, jobData, isFirstJob,isGeneratedCheckNumber,isCheckNumber) => {
    let claims = [];
    
    if (values?.claims?.length > 0) {
      const isPullDataNeed = jobData?.grid_data?.transaction_type !== "PATIENT_PAY_835";
      claims = handleClaimsAutomationOptimized(values, "PAYMENT835", isPullDataNeed)?.claims || [];
    } else {
      claims = [createInitialClaim(jobData, values,true)];
    }

    const processedPlbAdjustments = processPlbAdjustments(values?.plb_adjustments);
    const { remaining } = calculateRemainingPlbPaidTotals(claims, values?.total_plb_amt,values?.check_amount);

    const { payment_method, check_number } = generatePaymentData(
    paymentType, 
    isCheckNumber,
    jobData,
    values,
    );

    const check_amount = 
    (jobData?.is_worked === false && window.location.pathname === NEW_DATA_ENTRY &&
      (!values?.check_amount || values?.check_amount === "0.00"))
      ? parseFloat(0).toFixed(2) :
      values?.check_amount === 0 ? parseFloat(0).toFixed(2) : values?.check_amount ? parseFloat(values?.check_amount || 0).toFixed(2) : ""
    
    if(!(isCheckNumber === check_number) && isQaScreen){
        if(Object.keys(values?.edited_data)?.length>0){
            values.edited_data.check_number = {
            ...(values.edited_data.check_number || {}),
            old_val: isCheckNumber,
            error: true
            };
        } 
    }
    const updatedValues = {
      ...values,
      transaction_type:paymentType,
      payment_method: payment_method,
      check_number,
      check_amount,
      remaining_balance: remaining,
      job: jobData?.job,
      client_id: jobData?.client_id,
      is_first_sub_job: isFirstJob,

      // Map values with fallbacks
      ...Object.fromEntries([
        'payment_id', 'check_date', 'check_page_number', 'correspondance',
        'payee_id', 'payee_name', 'payee_npi', 'tax_identification_number', 'payee_address',
        'payee_city', 'payee_state', 'payee_zip', 'payer_id', 'payer_name', 'payer_address',
        'payer_city', 'payer_state', 'payer_zip'
      ].map(field => [field, values?.[field] || ""])),

      payer_page_from: values?.payer_page_from || (pathname === NEW_DATA_ENTRY ? "1" : ""),
      payer_page_to: values?.payer_page_to || (pathname === NEW_DATA_ENTRY ? "1" : ""),
      payee_page_from: values?.payee_page_from || (pathname === NEW_DATA_ENTRY ? "1" : ""),
      payee_page_to: values?.payee_page_to || (pathname === NEW_DATA_ENTRY ? "1" : ""),
      qdx_status: values?.qdx_status || "",
      qdx_reason: values?.qdx_reason?.length > 0 ? values.qdx_reason : [],
      qdx_classification: values?.qdx_classification || "",
      plb_adjustments: processedPlbAdjustments,
      claims:[claims[0]],
    };
    
    updateMasterValues(updatedValues);
    setFullClaimData(claims)
    setFullClaimTotal([])
    resetToInitailStates()
    triggerEobValidation()
  };

  const handleSubJobTransaction = async (paymentType,currentValues, values, jobData, isFirstJob,isGeneratedCheckNumber,isCheckNumber) => {
    let claims = [];

    if (values?.claims?.length > 0) {
      const automationResult = handleClaimsAutomationOptimized({ claims: values.claims, ...values }, "CORRESPONDENCE_835", true);
      claims = automationResult.claims;

      // Handle 837 pull logic
      claims.forEach(claim => {
        if (claim?.encounter && claim.encounter !== '' && 
            jobData?.grid_data?.transaction_type !== "PATIENT_PAY_835") {
          localStorage.setItem('837_pulled', true);
        }
      });
    } else {
      claims = [createInitialClaim(jobData, values,true)];
    }

    const processedPlbAdjustments = processPlbAdjustments(values?.plb_adjustments);
    const { remaining } = calculateRemainingPlbPaidTotals(claims, values?.total_plb_amt,values?.check_amount);

    const { payment_method, check_number } = generatePaymentData(
    paymentType,
    isCheckNumber,
    jobData,
    values,
    );

    const checkAmount = isSubJob && !isFirstJob && pathname === NEW_DATA_ENTRY 
      ? values?.check_amount 
      : parseFloat(values?.check_amount || 0).toFixed(2);

    const updatedValues = {
      ...currentValues,
      transaction_type: paymentType,
      job: jobData?.job,
      client_id: jobData?.client_id,
      is_first_sub_job: isFirstJob,
      payment_method: payment_method,
      check_amount: checkAmount,
      remaining_balance: currentValues?.grid_customization !== "PLB_ONLY" 
        ? remaining 
        : getPlbOnlyRemainingBalance(processedPlbAdjustments, checkAmount),

      check_number: check_number,

      check_date: (isSubJob && !isFirstJob) ? values?.check_date : (values?.check_date || ""),

      // Map other fields
      ...Object.fromEntries([
        'payment_id', 'transaction_type', 'check_page_number', 'correspondance',
        'payee_id', 'payee_name', 'payee_npi', 'tax_identification_number', 'payee_address',
        'payee_city', 'payee_state', 'payee_zip', 'payer_id', 'payer_name', 'payer_address',
        'payer_city', 'payer_state', 'payer_zip'
      ].map(field => [field, values?.[field] || ""])),

      payer_page_from: values?.payer_page_from || (pathname === NEW_DATA_ENTRY ? "1" : ""),
      payer_page_to: values?.payer_page_to || (pathname === NEW_DATA_ENTRY ? "1" : ""),
      payee_page_from: values?.payee_page_from || (pathname === NEW_DATA_ENTRY ? "1" : ""),
      payee_page_to: values?.payee_page_to || (pathname === NEW_DATA_ENTRY ? "1" : ""),
      qdx_status: values?.qdx_status || "",
      qdx_reason: values?.qdx_reason?.length > 0 ? values.qdx_reason : [],
      qdx_classification: values?.qdx_classification || "",
      plb_adjustments: processedPlbAdjustments,
      edited_data:values?.edited_data || {},
      claims:[claims[0]],
    };
    setFullClaimData(claims)
    setFullClaimTotal([])
    updateMasterValues(updatedValues);
    resetToInitailStates()
    triggerEobValidation()
  };

  const handlePatientPay835FirstJob = async (paymentType,currentValues, values, jobData, isFirstJob, isCheckNumber, isGeneratedCheckNumber) => {

    // Payment method logic for Patient Pay
    const { payment_method, check_number } = generatePaymentData(
        paymentType, // "PATIENT_PAY_835", "PAYMENT835", or "CORRESPONDENCE_835"
        isCheckNumber,
        jobData,
        values,
        );

    // Process claims for Patient Pay
    let claims = [];
    if (jobData?.grid_data?.claims?.length > 0) {
      const automationResult = handleClaimsAutomationOptimized(values, "PATIENT_PAY_835", true);
      claims = automationResult.claims;

      // Handle 837 pull logic for patient pay
      claims.forEach(claim => {
        if (claim?.encounter && claim?.encounter !== '' && jobData?.grid_data?.transaction_type === "PATIENT_PAY_835") {
          localStorage.setItem('837_pulled', true);
        }
      });
    } else {
      claims = [createInitialClaim(jobData, values,false)];
    }

    // Calculate totals for Patient Pay
    let remaining = 0;
    let paid_total = 0;
    let plb_total = 0;

    claims.forEach((claim) => {
      paid_total = parseFloat(paid_total || 0) + parseFloat(claim.claim_paid || 0);
    });

    const processedPlbAdjustments = processPlbAdjustments(values?.plb_adjustments);
    
    processedPlbAdjustments.forEach((plb) => {
      plb_total = parseFloat(plb_total || 0) + parseFloat(plb?.plb_amt || 0);
    });

    remaining = parseFloat(values.check_amount || 0) - parseFloat(paid_total || 0) + parseFloat(plb_total || 0);

    if (parseFloat(remaining).toFixed(2) == -0.00) {
      remaining = parseFloat(0).toFixed(2);
    }

    const plbOnlyCheckAmnt = payment_method === "NON" ? parseFloat(0).toFixed(2) : values?.check_amount ? parseFloat(values?.check_amount).toFixed(2) : "";

    const updatedValues = {
      ...currentValues,
      payment_method: payment_method,
      check_amount: payment_method === "NON" ? parseFloat(0).toFixed(2) : values?.check_amount ? parseFloat(values?.check_amount).toFixed(2) : "",
      remaining_balance: formValues?.grid_customization != "PLB_ONLY" ? parseFloat(remaining || 0).toFixed(2) : getPlbOnlyRemainingBalance(processedPlbAdjustments, plbOnlyCheckAmnt),
      check_number: check_number,
      transaction_type: "PATIENT_PAY_835",
      job: jobData?.job,
      client_id: jobData?.client_id,
      is_first_sub_job: isFirstJob,

      // Map basic fields
      payment_id: values?.payment_id || "",
      check_date: isSubJob && !isFirstJob ? values?.check_date : (values?.check_date || ""),
      check_page_number: values?.check_page_number || "",
      correspondance: values?.correspondance || "",

      // Payee and Payer info
      ...Object.fromEntries([
        'payee_id', 'payee_name', 'payee_npi', 'tax_identification_number', 'payee_address',
        'payee_city', 'payee_state', 'payee_zip', 'payer_id', 'payer_name', 'payer_address',
        'payer_city', 'payer_state', 'payer_zip'
      ].map(field => [field, values?.[field] || ""])),

      payer_page_from: values?.payer_page_from || (pathname == NEW_DATA_ENTRY ? "1" : ""),
      payer_page_to: values?.payer_page_to || (pathname == NEW_DATA_ENTRY ? "1" : ""),
      payee_page_from: values?.payee_page_from || (pathname == NEW_DATA_ENTRY ? "1" : ""),
      payee_page_to: values?.payee_page_to || (pathname == NEW_DATA_ENTRY ? "1" : ""),

      qdx_status: values?.qdx_status || "",
      qdx_reason: values?.qdx_reason?.length > 0 ? values?.qdx_reason : [],
      qdx_classification: values?.qdx_classification || "",
      plb_adjustments: processedPlbAdjustments,
      claims:[claims[0]],
    };
    setFullClaimData(claims)
    setFullClaimTotal([])
    updateMasterValues(updatedValues);
    resetToInitailStates()
    triggerEobValidation()
  };

  const handlePatientPay835SubJob = async (paymentType,currentValues, values, jobData, isFirstJob, isCheckNumber, isGeneratedCheckNumber) => {
    let claims = [];
    
    if (jobData?.grid_data?.claims?.length > 0) {
      const automationResult = handleClaimsAutomationOptimized(jobData?.grid_data, "PATIENT_PAY_835", true);
      claims = automationResult.claims;

      // Handle 837 pull logic
      claims.forEach(claim => {
        if (claim?.encounter && claim?.encounter !== '' && jobData?.grid_data?.transaction_type === "PATIENT_PAY_835") {
          localStorage.setItem('837_pulled', true);
        }
      });
    } else {
      claims = [createInitialClaim(jobData, values)];
    }

    const processedPlbAdjustments = processPlbAdjustments(values?.plb_adjustments);
    const plbOnlyCheckAmnt = isSubJob && !isFirstJob && pathname == NEW_DATA_ENTRY ? values?.check_amount :
      values?.check_amount == 0 ? parseFloat(0).toFixed(2) : 
      parseFloat(values?.check_amount || 0).toFixed(2);

    // Main function call
    const { payment_method, check_number } = generatePaymentData(
    paymentType, 
    isCheckNumber,
    jobData,
    values,
    );
    const updatedValues = {
      ...formValues,
      transaction_type: "PATIENT_PAY_835",
      job: jobData?.job,
      client_id: jobData?.client_id,
      is_first_sub_job: isFirstJob,

      payment_id: values?.payment_id || "",
      payment_method,

      check_number,

      check_date: isSubJob && !isFirstJob ? values?.check_date : (values?.check_date || ""),
      check_amount: plbOnlyCheckAmnt,
      remaining_balance: formValues?.grid_customization != "PLB_ONLY" ? 
        parseFloat(0).toFixed(2) : getPlbOnlyRemainingBalance(processedPlbAdjustments, plbOnlyCheckAmnt),

      // Other fields
      check_page_number: values?.check_page_number || "",
      correspondance: values?.correspondance || "",

      // Payee and Payer info
      ...Object.fromEntries([
        'payee_id', 'payee_name', 'payee_npi', 'tax_identification_number', 'payee_address',
        'payee_city', 'payee_state', 'payee_zip', 'payer_id', 'payer_name', 'payer_address',
        'payer_city', 'payer_state', 'payer_zip'
      ].map(field => [field, values?.[field] || ""])),

      payer_page_from: values?.payer_page_from || (pathname == NEW_DATA_ENTRY ? "1" : ""),
      payer_page_to: values?.payer_page_to || (pathname == NEW_DATA_ENTRY ? "1" : ""),
      payee_page_from: values?.payee_page_from || (pathname == NEW_DATA_ENTRY ? "1" : ""),
      payee_page_to: values?.payee_page_to || (pathname == NEW_DATA_ENTRY ? "1" : ""),
      qdx_status: values?.qdx_status || "",
      qdx_reason: values?.qdx_reason?.length > 0 ? values?.qdx_reason : [],
      qdx_classification: values?.qdx_classification || "",
      plb_adjustments: processedPlbAdjustments,
      claims:[claims[0]],
    };
    setFullClaimData(claims)
    setFullClaimTotal([])
    updateMasterValues(updatedValues);
    resetToInitailStates()
    triggerEobValidation()
  };
  
const handlePayment835LiteJob = async (paymentType, values, jobData, isFirstJob, isCheckNumber, isGeneratedCheckNumber) => {

    const structuredValues = structuredClone(jobData?.grid_data);

    let payment_method = "";
    if (window.location.pathname === NEW_DATA_ENTRY) {
        if (structuredValues.check_amount == parseFloat(0).toFixed(2)) {
            payment_method = "NON";
        } else if (parseFloat(structuredValues.check_amount) > parseFloat(0)) {
            payment_method = "CHK";
        } else {
            payment_method = "ACH";
        }
    } else {
        payment_method = structuredValues.payment_method && ['ACH', 'CHK', 'NON', 'VCP'].includes(structuredValues.payment_method) ? structuredValues.payment_method : "";
    }

    let claims = [];
    const isAutomationDataLoad = jobData?.is_automation_completed && jobData?.is_automation_job;

    if (jobData && structuredValues && (structuredValues?.transaction_type === "PAYMENT835_LITE" || isAutomationDataLoad)) {
        let plb_adjustments = structuredValues?.plb_adjustments;

        if (plb_adjustments && plb_adjustments?.length > 0) {
            plb_adjustments.map((plb, index) => {
                plb.plb_index = index;
                plb.plb_amt = (plb.plb_amt === "" || plb.plb_amt === undefined) ? "" : parseFloat(plb.plb_amt || 0).toFixed(2);
            });
        }
        if (structuredValues.claims?.length > 0) {
            claims = structuredValues?.claims;
            claims.map((claim, index) => {
                claim.claim_id = claim?.claim_id || "";
                claim.order = index || "";
                claim.patient_id = claim?.patient_id || "";
                claim.patient_account_number = claim?.patient_account_number || "";
                claim.patient_first_name = claim?.patient_first_name || "";
                claim.patient_middle_name = claim?.patient_middle_name || "";
                claim.patient_last_name = claim?.patient_last_name || "";
                claim.page_from = claim.page_from || (window.location.pathname === NEW_DATA_ENTRY ? "1" : "");
                claim.page_to = claim.page_to || (window.location.pathname === NEW_DATA_ENTRY ? "1" : "");

                // if (!Array.isArray(claim?.service_lines) && claim?.service_lines) {
                //     const service_line_dict = {
                //         service_line_id : claim?.service_lines?.[0]?.service_line_id || "",
                //         period_start : claim?.service_lines?.[0]?.period_start ? moment(claim.service_lines?.period_start).format("MM/DD/YYYY") : "",
                //         period_end : claim?.service_lines?.[0]?.period_end ? moment(claim.service_lines?.period_end).format("MM/DD/YYYY") : "",
                //         billed : parseFloat(claim?.service_lines?.[0]?.billed || 0).toFixed(2),
                //         paid : parseFloat(claim?.service_lines?.[0]?.paid || 0).toFixed(2),

                //     }
                //     claim.service_lines = [{...claim?.service_lines,...service_line_dict}]
                // } else
                if (Array.isArray(claim?.service_lines) && claim?.service_lines?.length > 0) {
                    const billed = claim.service_lines.reduce((acc, serviceLine) => acc + (Number.isNaN(parseFloat(serviceLine.billed)) ? 0 : parseFloat(serviceLine.billed)), 0).toFixed(2);
                    const paid = claim.service_lines.reduce((acc, serviceLine) => acc + (Number.isNaN(parseFloat(serviceLine.paid)) ? 0 : parseFloat(serviceLine.paid)), 0).toFixed(2);
                    const service_lines = {};
                    service_lines.service_line_id = claim?.service_lines[0]?.service_line_id||"";
                    service_lines.period_start = claim?.period_start ? moment(claim?.period_start).format("MM/DD/YYYY") : "";
                    service_lines.period_end = claim?.period_end ? moment(claim?.period_end).format("MM/DD/YYYY") : "";
                    service_lines.billed = billed;
                    service_lines.paid = paid;
                    claim.service_lines = [service_lines];
                } else {
                    claim.service_lines =[{
                        service_line_id:"",
                        billed: parseFloat(0).toFixed(2),
                        paid: parseFloat(0).toFixed(2),
                        period_start: "",
                        period_end: ""
                    }];
                }
            });
        } else {
            claims = [{
                edited:true,
                claim_id: "",
                order: "",
                patient_id: "",
                patient_account_number: "",
                patient_first_name: "",
                patient_middle_name: "",
                patient_last_name: "",
                page_from: window.location.pathname === NEW_DATA_ENTRY ? "1" : "",
                page_to: window.location.pathname === NEW_DATA_ENTRY ? "1" : "",
                service_lines: [{
                    edited:true,
                    service_line_id: "",
                    period_start: "",
                    period_end: "",
                    billed: "",
                    paid: "",
                }],
            }];
        }
    } else {
        claims = [{
            claim_id: "",
            order: "",
            patient_id: "",
            patient_account_number: "",
            patient_first_name: "",
            patient_middle_name: "",
            patient_last_name: "",
            page_from: window.location.pathname === NEW_DATA_ENTRY ? "1" : "",
            page_to: window.location.pathname === NEW_DATA_ENTRY ? "1" : "",
            service_lines: [{
                service_line_id: "",
                period_start: "",
                period_end: "",
                billed: "",
                paid: "",
            }],
        }];
    }
    const processedPlbAdjustments = structuredValues?.plb_adjustments;

    let remaining = 0;
    let paid_total = 0;
    let plb_total = jobData?.grid_data?.total_plb_amt;

    claims.forEach((claim) => {
        paid_total += parseFloat(claim?.service_lines?.[0]?.paid || 0);
    });

    // if (structuredValues?.transaction_type === "PAYMENT835_LITE" && processedPlbAdjustments?.length > 0) {
    //     processedPlbAdjustments.forEach((plb) => {
    //         plb_total += parseFloat(plb?.plb_amt || 0);
    //     });
    // }

    remaining = parseFloat(structuredValues.check_amount || 0) - parseFloat(paid_total || 0) + parseFloat(plb_total || 0);

    if (parseFloat(remaining).toFixed(2) === "-0.00") {
        remaining = parseFloat(0).toFixed(2);
    }
    
    const { check_number } = generatePaymentData(
        paymentType,
        isCheckNumber,
        jobData,
        structuredValues,
    );

    const check_amount = (jobData?.is_worked === false && window.location.pathname === NEW_DATA_ENTRY &&
        (!structuredValues?.check_amount || structuredValues?.check_amount === "0.00")) ?
        parseFloat(0).toFixed(2) :
        structuredValues?.check_amount === 0 ? parseFloat(0).toFixed(2) : structuredValues?.check_amount ? parseFloat(structuredValues?.check_amount || 0).toFixed(2) : ""

    const updatedValues = {
        ...structuredValues,
        grid_customization: "NORMAL",
        transaction_type: paymentType,
        payment_method: payment_method,
        check_number,
        check_amount,
        remaining_balance: parseFloat(remaining).toFixed(2),
        job: jobData?.job,
        client_id: jobData?.client_id,
        is_first_sub_job: isFirstJob,
        // Only include fields relevant to PAYMENT835_LITE
        payment_id: structuredValues?.payment_id || "",
        check_date: structuredValues?.check_date ? moment(structuredValues?.check_date).format("MM/DD/YYYY") : "",
        check_page_number: structuredValues?.check_page_number || "",
        correspondance: structuredValues?.correspondance || "",
        payer_id: structuredValues?.payer_id || "",
        payer_name: structuredValues?.payer_name || "",
        payer_page_from: structuredValues?.payer_page_from || (pathname === NEW_DATA_ENTRY ? "1" : ""),
        payer_page_to: structuredValues?.payer_page_to || (pathname === NEW_DATA_ENTRY ? "1" : ""),
        payee_id: structuredValues?.payee_id || "",
        payee_name: structuredValues?.payee_name || "",
        payee_page_from: structuredValues?.payee_page_from || (pathname === NEW_DATA_ENTRY ? "1" : ""),
        payee_page_to: structuredValues?.payee_page_to || (pathname === NEW_DATA_ENTRY ? "1" : ""),
        qdx_status: structuredValues?.qdx_status || "",
        qdx_reason: structuredValues?.qdx_reason?.length > 0 ? structuredValues.qdx_reason : [],
        qdx_classification: structuredValues?.qdx_classification || "",
        is_orphaned_check: structuredValues.is_orphaned_check || false,
        is_missing_check_image: structuredValues.is_missing_check_image || false,
        is_forced_balancing: structuredValues.is_forced_balancing || false,
        is_payer_black_listing: structuredValues.is_payer_black_listing || false,
        is_payer_excluding_indexing: structuredValues.is_payer_excluding_indexing || false,
        payer_account_number: structuredValues?.payer_account_number || '',
        aba_routing_number: structuredValues?.aba_routing_number || '',
        plb_adjustments: processedPlbAdjustments,
        claims: [claims[0]],
    };

    updateMasterValues(updatedValues);
    setFullClaimData(claims);
    setFullClaimTotal([])
    resetToInitailStates();
    // triggerEobValidation();
};
const handleCorresJob = async (values, jobData, isFirstJob) => {
    let claims = [];
    const formCorres = structuredClone(jobData?.grid_data);
    const isAutomation = (jobData?.is_automation_job && jobData?.is_automation_completed);

    if (jobData && values && (values?.transaction_type === "CORRESPONDENCE" || (isAutomation))) {
        if (values?.claims && values?.claims?.length > 0) {
            claims = values?.claims.map((claim, index) => {
                const updatedClaim = {
                    ...claim,
                    claim_id: claim?.claim_id || "",
                    order: claim?.claim_id || "",
                    patient_account_number: claim?.patient_account_number || "",
                    period_start: claim?.period_start ? moment(claim?.period_start).format("MM/DD/YYYY") : "",
                    period_end: claim?.period_end ? moment(claim?.period_end).format("MM/DD/YYYY") : "",
                    patient_id: claim?.patient_id || "",
                    patient_first_name: claim?.patient_first_name || "",
                    patient_middle_name: claim?.patient_middle_name || "",
                    patient_last_name: claim?.patient_last_name || "",
                    page_from: claim?.page_from || (window.location.pathname === NEW_DATA_ENTRY ? "1" : ""),
                    page_to: claim?.page_to || (window.location.pathname === NEW_DATA_ENTRY ? "1" : ""),
                };

                let automationBuild = null;
                if (isAutomation && updatedClaim?.service_lines && Array.isArray(updatedClaim?.service_lines)) {
                    automationBuild = updatedClaim.service_lines.reduce((acc, i) => acc + (parseFloat(i.billed) || 0), 0);
                }
                updatedClaim.billed = automationBuild ? parseFloat(Number(automationBuild) || 0).toFixed(2) : (parseFloat(updatedClaim.billed || 0).toFixed(2));
                
                return updatedClaim;
            });
        } else {
            claims = [{
                edited:true,
                claim_id: "",
                order: "1",
                patient_account_number: "",
                period_start: "",
                period_end: "",
                billed: "",
                patient_id: "",
                patient_first_name: "",
                patient_middle_name: "",
                patient_last_name: "",
                page_from: jobData?.is_large_batch_job && jobData?.header_range?.length > 0 ? jobData?.header_range.length + 2 : window.location.pathname === NEW_DATA_ENTRY ? "1" : "",
                page_to: jobData?.is_large_batch_job && jobData?.header_range?.length > 0 ? jobData?.header_range.length + 2 : window.location.pathname === NEW_DATA_ENTRY ? "1" : "",
            }];
        }
    } else {
        claims = [{
            claim_id: "",
            order: "1",
            patient_account_number: "",
            period_start: "",
            period_end: "",
            billed: "",
            patient_id: "",
            patient_first_name: "",
            patient_middle_name: "",
            patient_last_name: "",
            page_from: jobData?.is_large_batch_job && jobData?.header_range?.length > 0 ? jobData?.header_range.length + 2 : window.location.pathname === NEW_DATA_ENTRY ? "1" : "",
            page_to: jobData?.is_large_batch_job && jobData?.header_range?.length > 0 ? jobData?.header_range.length + 2 : window.location.pathname === NEW_DATA_ENTRY ? "1" : "",
        }];
    }

    const updatedValues = {
        total_claims:values?.total_claims,
        job: jobData?.job,
        payment_id: values?.payment_id || null,
        payer_name: values?.payer_name || "",
        payer_page_from: values?.payer_page_from || (window.location.pathname === NEW_DATA_ENTRY ? "1" : ""),
        payer_page_to: values?.payer_page_to || (window.location.pathname === NEW_DATA_ENTRY ? "1" : ""),
        is_first_sub_job: isFirstJob,
        letter_date: (values?.check_date || values?.letter_date) || "",
        correspondance: values?.correspondance || "",
        qdx_status: values?.qdx_status || "",
        qdx_reason: (values?.qdx_reason?.length > 0 ? values.qdx_reason : []),
        qdx_classification: values?.qdx_classification || "",
        is_orphaned_check: values.is_orphaned_check || false,
        is_missing_check_image: values.is_missing_check_image || false,
        is_forced_balancing: values.is_forced_balancing || false,
        is_payer_black_listing: values.is_payer_black_listing || false,
        is_payer_excluding_indexing: values.is_payer_excluding_indexing || false,
        transaction_type: "CORRESPONDENCE",
      edited_data: {
        payer_name: values?.edited_data?.payer_name || {},
        payer_page_from: values?.edited_data?.payer_page_from || {},
        payer_page_to: values?.edited_data?.payer_page_to || {},
        letter_date: values?.edited_data?.letter_date || {}, 
        correspondance: values?.edited_data?.correspondance || {},
      },
        claims: [claims[0]],
    };

    updateMasterValues(updatedValues);
    setFullClaimData(claims);
    setFullClaimTotal([])
    resetToInitailStates();
};
const handlePatientPayJob = async (values, jobData) => {

    const updatedValues = {
        transaction_type:"PATIENT_PAY",
        job: jobData?.job,
        check_number: values?.check_number || "",
        check_date: values?.check_date || "",
        is_check_amount: values?.check_amount ? parseFloat(values.check_amount).toFixed(2) : "",
        check_amount: (values?.check_amount !== '' && values?.check_amount !== undefined && values?.check_amount !== null) ? parseFloat(values.check_amount).toFixed(2) : "",
        payment_id: values?.payment_id || "",
        patient_account_number: values?.patient_account_number || "",
        patient_first_name: values?.patient_first_name || "",
        patient_middle_name: values?.patient_middle_name || "",
        patient_last_name: values?.patient_last_name || "",
        patient_page_from: values?.patient_page_from || (window.location.pathname === NEW_DATA_ENTRY ? "1" : ""),
        patient_page_to: values?.patient_page_to || (window.location.pathname === NEW_DATA_ENTRY ? "1" : ""),
        amount_due: parseFloat(values?.amount_due || 0) ? parseFloat(values.amount_due).toFixed(2) : "",
        statement_number: values?.statement_number || "",
        statement_amount: parseFloat(values?.statement_amount || 0) ? parseFloat(values.statement_amount).toFixed(2) : "",
        statement_date: values?.statement_date || "",
        qdx_status: values?.qdx_status || "",
        qdx_reason: values?.qdx_reason?.length > 0 ? values.qdx_reason : [],
        is_orphaned_check: values.is_orphaned_check || false,
        is_missing_check_image: values.is_missing_check_image || false,
        is_forced_balancing: values.is_forced_balancing || false,
        is_payer_black_listing: values.is_payer_black_listing || false,
        is_payer_excluding_indexing: values.is_payer_excluding_indexing || false,
        payer_account_number: values.payer_account_number || '',
        aba_routing_number: values.aba_routing_number || '',
        qdx_classification: values?.qdx_classification || "",
      edited_data: {
        check_number: values?.edited_data?.check_number ? values?.edited_data?.check_number : {},
        check_date: values?.edited_data?.check_date ? values?.edited_data?.check_date : {},
        is_check_amount: values?.edited_data?.is_check_amount ? values?.edited_data?.is_check_amount : {},
        check_amount: values?.edited_data?.check_amount ? values?.edited_data?.check_amount : {},
        payment_id: values?.edited_data?.payment_id ? values?.edited_data?.payment_id : {},
        patient_account_number: values?.edited_data?.patient_account_number ? values?.edited_data?.patient_account_number : {},
        patient_first_name: values?.edited_data?.patient_first_name ? values?.edited_data?.patient_first_name : {},
        patient_middle_name: values?.edited_data?.patient_middle_name ? values?.edited_data?.patient_middle_name : {},
        patient_last_name: values?.edited_data?.patient_last_name ? values?.edited_data?.patient_last_name : {},
        patient_page_from: values?.edited_data?.patient_page_from ? values?.edited_data?.patient_page_from : {},
        patient_page_to: values?.edited_data?.patient_page_to ? values?.edited_data?.patient_page_to : {},
        amount_due: values?.edited_data?.amount_due ? values?.edited_data?.amount_due : {},
        statement_number: values?.edited_data?.statement_number ? values?.edited_data?.statement_number : {},
        statement_amount: values?.edited_data?.statement_amount ? values?.edited_data?.statement_amount : {},
        statement_date: values?.edited_data?.statement_date ? values?.edited_data?.statement_date : {},
        qdx_status: values?.edited_data?.qdx_status ? values?.edited_data?.qdx_status : {},
        qdx_reason: values?.edited_data?.qdx_reason ? values?.edited_data?.qdx_reason : {},
        is_orphaned_check: values?.edited_data?.is_orphaned_check ? values?.edited_data?.is_orphaned_check : {},
        is_missing_check_image: values?.edited_data?.is_missing_check_image ? values?.edited_data?.is_missing_check_image : {},
        is_forced_balancing: values?.edited_data?.is_forced_balancing ? values?.edited_data?.is_forced_balancing : {},
        is_payer_black_listing: values?.edited_data?.is_payer_black_listing ? values?.edited_data?.is_payer_black_listing : {},
        is_payer_excluding_indexing: values?.edited_data?.is_payer_excluding_indexing ? values?.edited_data?.is_payer_excluding_indexing : {},
        payer_account_number: values?.edited_data?.payer_account_number ? values?.edited_data?.payer_account_number : {},
        aba_routing_number: values?.edited_data?.aba_routing_number ? values?.edited_data?.aba_routing_number : {},
        qdx_classification: values?.edited_data?.qdx_classification ? values?.edited_data?.qdx_classification : {},
      }
    };

    updateMasterValues(updatedValues);
    setFullClaimData([]);
    resetToInitailStates();
};
  return { 
    calculateRemainingPlbPaidTotals,
    transactionPayment835, 
    transactionPayment835Lite, // New function for the lite transaction type
    transactionPatientPay835,
    transactionPatientPay,
    transactionCorrespondance,
    processClaimsAutomation: handleClaimsAutomationOptimized,
    generatePaymentData
  };
};